/**
 * Voice runtime — drives `machine.ts` from real microphone capture and the relay socket, and exposes the ⌘K composer's
 * text mode over the SAME orchestrator (R-02: one brain, two mouths).
 *
 * Three pieces, each honest about what it is:
 *
 *   `capture.ts`  real. Microphone, levels, endpointing. Arithmetic on samples; needs no credential.
 *   `transcribe.ts`  simulated, and says so on every result. Turning audio into words is a provider call and this
 *                    build has no ASR credential.
 *   `relay.ts`  real. A socket to *our* relay, opened with a 60-second device-bound ticket, carrying the turn to the
 *               same `runTurn` the composer calls.
 *
 * The relay is preferred for spoken turns because it streams and because barge-in is a message on it. When it cannot
 * be opened — no device, an expired ticket, a server that is not there — the turn goes over `voice.composer.ask`
 * instead. Same orchestrator either way, so the fallback changes the transport and nothing else.
 */
import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { api } from '@/client/api';
import { setModel } from '@/store/store';
import { SCREEN_MODE } from '@/model/nav';
import type { ScreenId } from '@/model/state';
import { createCapture, type Capture } from './capture';
import { connectRelay, type RelayConnection, type RelayServerEvent } from './relay';
import { createSimTranscriber, type Transcriber } from './transcribe';
import { initialVoice, voiceReduce, type VoiceContext, type VoiceEvent } from './machine';

const SILENCE_TICK_MS = 500;

const turnKey = () => `vox-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

export interface VoiceApi {
  ctx: VoiceContext;
  /** Push-to-talk press/release (also the barge-in gesture while speaking). */
  press(): void;
  release(): void;
  /** ⌘K text mode — same orchestrator, same skills, same guardrails. */
  ask(text: string, confirm?: boolean): Promise<void>;
  confirm(): Promise<void>;
  /** Dismisses a standing recoverable fault — "Try again" on the chip; pressing the mic clears it too. */
  clearFault(): void;
  /** Raw machine-event dispatch, for the dev/test hook only — the visreg state forcing rides the real reducer. */
  send(event: VoiceEvent): void;
  /** Dev/test only: pin a machine-shaped context so a screenshot can catch a state time would move on. */
  force(ctx: Partial<VoiceContext> | null): void;
  end(): void;
  /** Whether the HUD should be on screen at all. */
  active: boolean;
}

export interface VoiceRuntimeOptions {
  /** Injected in tests. Production builds take the browser microphone and a real WebSocket. */
  readonly capture?: Capture;
  readonly transcriber?: Transcriber;
  readonly connect?: typeof connectRelay;
  readonly enabled?: boolean;
  /** How long `thinking` may wait before it becomes a recoverable provider_timeout fault. */
  readonly providerTimeoutMs?: number;
}

/** The assignable push-to-talk key (ESC-003). Stored per browser; the HUD's own "key" affordance writes it. A
 *  key is a KeyboardEvent.code ('Space', 'KeyV', …) so layouts cannot shift it. */
const PTT_KEY_STORAGE = 'pf.pttKey';
export function pttKey(): string {
  try {
    return window.localStorage.getItem(PTT_KEY_STORAGE) ?? 'Space';
  } catch {
    return 'Space';
  }
}
export function setPttKey(code: string): void {
  try {
    window.localStorage.setItem(PTT_KEY_STORAGE, code);
  } catch {
    /* a browser that blocks storage keeps the default — the feature degrades, never breaks */
  }
}

/** Whether a key event belongs to typing rather than to the desk. Push-to-talk must never eat a character. */
const isTyping = (target: EventTarget | null): boolean => {
  const el = target as HTMLElement | null;
  return (
    !!el &&
    (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT' || el.isContentEditable)
  );
};

export function useVoice(options: VoiceRuntimeOptions = {}): VoiceApi {
  const [reduced, dispatch] = useReducer(voiceReduce, undefined, () => initialVoice());
  // Dev/test only: a pinned context for the visreg state baselines (SM-P6-01). The HUD is the real component and
  // the shape is the real machine's; what a pin removes is TIME — the effects that would move `thinking` and
  // `speaking` on before a screenshot lands. Compiled out of production builds with the rest of the dev surface.
  const [forced, setForced] = useState<VoiceContext | null>(null);
  const ctx = forced ?? reduced;
  const [, setTurns] = useState(0);
  const asking = useRef(false);
  const relay = useRef<RelayConnection | null>(null);
  const answer = useRef<string[]>([]);
  const started = useRef(0);
  const send = useCallback((e: VoiceEvent) => dispatch(e), []);

  const transcriber = useMemo(() => options.transcriber ?? createSimTranscriber(), [options.transcriber]);

  // session state from the port: lane, minutes, degrade banner
  useEffect(() => {
    let cancelled = false;
    void api.voice.session.state().then((s) => {
      if (cancelled) return;
      send({ type: 'minutes', used: s.minutes.used, quota: s.minutes.quota });
      if (s.degradeBanner) send({ type: 'degrade', lane: s.lane, reason: s.degradeBanner });
    });
    return () => {
      cancelled = true;
    };
  }, [send]);

  // 8s silence closes a listening turn (SDD §20.4). Real capture endpoints on its own; this is the backstop for a
  // session with no microphone, where nothing is ever heard and the turn would otherwise wait for ever.
  useEffect(() => {
    if (ctx.state !== 'listening') return;
    const t = setInterval(() => send({ type: 'silence', ms: SILENCE_TICK_MS }), SILENCE_TICK_MS);
    return () => clearInterval(t);
  }, [ctx.state, send]);

  const onRelayEvent = useCallback(
    (event: RelayServerEvent) => {
      switch (event.type) {
        case 'ready':
          send({ type: 'minutes', used: event.minutes.used, quota: event.minutes.quota });
          if (event.minutes.exhausted)
            send({
              type: 'degrade',
              lane: 'text',
              reason: 'Voice minutes are used up for this cycle — answers are text-only until it resets.',
            });
          return;
        case 'transcript':
          // Assistant chunks are accumulated and dispatched whole. The chunking exists so that an interrupt lands
          // *between* pieces on the server; the HUD renders a transcript line, and half a line is not one.
          if (event.role === 'assistant') answer.current.push(event.text);
          return;
        case 'turn.end':
          send({
            type: 'answer',
            at: new Date().toISOString(),
            text: answer.current.join(' '),
            latencyMs: Date.now() - started.current,
            confirmation: null,
          });
          answer.current = [];
          if (event.push) {
            const id = event.push.screen as ScreenId;
            setModel({ screen: id, mode: SCREEN_MODE[id] ?? 'analyze' });
          }
          return;
        case 'cancelled':
          // The local barge-in already moved the machine; this is the server agreeing. Dropping the accumulated
          // chunks is the part that matters — they belong to an answer nobody is going to hear.
          answer.current = [];
          return;
        case 'error':
          send({
            type: 'answer',
            at: new Date().toISOString(),
            text: event.message,
            latencyMs: 0,
            confirmation: null,
          });
          answer.current = [];
          return;
        default:
      }
    },
    [send],
  );

  const onRelayClosed = useCallback(() => {
    relay.current = null;
  }, []);

  /** Opens the socket, or returns null and lets the caller fall back to the REST turn.
   *
   *  A ticket is single-use, so this asks for a new one every time rather than caching: a cached ticket is a ticket
   *  that has already been spent. */
  const ensureRelay = useCallback(async (): Promise<RelayConnection | null> => {
    if (relay.current?.open) return relay.current;
    if (options.enabled === false) return null;
    try {
      const session = await api.auth.session.current();
      if (!session) return null;
      const issued = await api.voice.session.ticket({ deviceId: session.deviceId });
      // A simulated ticket names a relay that does not exist — `wss://voice.simulated.local/relay`, a host chosen
      // precisely so it can never resolve to anything real. Dialling it anyway costs a browser console error on
      // every voice session, which reads as a broken system while the system is doing exactly what it should. The
      // turn still runs: `runAsk` takes the REST path, which is the same fallback an expired ticket or a dropped
      // relay already lands on. Keyed on `meta.mode` rather than on the hostname, because the mode is the contract's
      // own statement about what answered and the hostname is a convention inside one binding (DEC-075).
      if (issued.meta.mode === 'simulated') return null;
      const connection = await (options.connect ?? connectRelay)(
        { ticket: issued.ticket, relayUrl: issued.relayUrl, deviceId: session.deviceId },
        { onEvent: onRelayEvent, onClosed: onRelayClosed },
      );
      relay.current = connection;
      return connection;
    } catch {
      // Every refusal — expired ticket, unknown device, no server — ends here as "use the other transport". The
      // trader still gets an answer; it just does not stream.
      return null;
    }
  }, [options.connect, options.enabled, onRelayEvent, onRelayClosed]);

  const runAsk = useCallback(
    async (text: string, confirm?: boolean) => {
      if (asking.current) return;
      asking.current = true;
      try {
        // A fresh key per turn. Deriving it from the text made asking the same question twice collide — and a
        // conversation is exactly where somebody asks the same thing twice. An idempotency key identifies a
        // *request*, not its content; reusing one is what a retry does, and this is not a retry.
        const res = await api.voice.composer.ask({ idempotencyKey: turnKey(), text, confirm });
        send({
          type: 'answer',
          at: res.meta.asOf,
          text: res.answer,
          latencyMs: res.latencyMs,
          confirmation: res.confirmation,
        });
        if (res.push) {
          const id = res.push.screen as ScreenId;
          setModel({ screen: id, mode: SCREEN_MODE[id] ?? 'analyze' });
        }
      } catch (error) {
        // A turn that failed still has to end, or the HUD sits in `thinking` for ever and the trader has no way to
        // tell a slow answer from a dead one.
        send({
          type: 'answer',
          at: new Date().toISOString(),
          text: error instanceof Error ? error.message : 'That did not go through. Try again.',
          latencyMs: 0,
          confirmation: null,
        });
      } finally {
        asking.current = false;
      }
    },
    [send],
  );

  // thinking → answer. The relay when it will open, the composer call when it will not — bounded: a provider that
  // answers nothing within the budget is a recoverable fault the trader can see, never a pill spinning for ever.
  useEffect(() => {
    if (forced) return;
    if (ctx.state !== 'thinking' || !ctx.pendingQuery) return;
    const text = ctx.pendingQuery;
    started.current = Date.now();
    const budget = setTimeout(() => {
      send({
        type: 'fault',
        kind: 'provider_timeout',
        message: 'The voice provider did not answer in time. Try again.',
      });
    }, options.providerTimeoutMs ?? 30_000);
    void (async () => {
      const connection = await ensureRelay();
      if (connection) {
        answer.current = [];
        connection.utter(text, turnKey());
        return;
      }
      await runAsk(text);
    })().finally(() => clearTimeout(budget));
    return () => clearTimeout(budget);
  }, [forced, ctx.state, ctx.pendingQuery, ensureRelay, runAsk, send, options.providerTimeoutMs]);

  // speaking → idle after the utterance is "spoken" (proportional to length, capped). Real TTS would end this on the
  // provider's own end-of-speech; there is no TTS credential, so the duration is estimated and the estimate is what
  // barge-in interrupts.
  useEffect(() => {
    if (forced) return;
    if (ctx.state !== 'speaking') return;
    const last = ctx.transcript[ctx.transcript.length - 1];
    const ms = Math.min(4000, 700 + (last?.text.length ?? 0) * 12);
    const t = setTimeout(() => send({ type: 'speech.end' }), ms);
    return () => clearTimeout(t);
  }, [forced, ctx.state, ctx.transcript, send]);

  /** Ends the captured utterance and moves the turn on. Shared by the button release and the VAD endpoint, because
   *  a hands-free endpoint and a released button mean exactly the same thing. */
  const endUtterance = useCallback(() => {
    void transcriber.finish().then((result) => {
      send({ type: 'partial', text: result.text });
      send({ type: 'endpoint', at: new Date().toISOString(), text: result.text });
    });
  }, [send, transcriber]);

  const capture = useMemo(
    () =>
      options.capture ??
      createCapture({
        // The dimmed partial appears when somebody actually starts talking, not when the button goes down. With no
        // ASR there is nothing to stream, so what appears is the simulated utterance — but *when* it appears is real.
        onSpeechStart: () => send({ type: 'partial', text: transcriber.partial() }),
        onEndpoint: () => endUtterance(),
        onTimeout: () => send({ type: 'end' }),
        // The directive's recoverable states, distinguished at the source — but WITHOUT killing a turn that can
        // still complete. A DENIED permission is a fault: the trader must act, and the chip says what to do. A
        // merely missing device degrades: the transcriber still carries the utterance and push-to-talk still
        // works, exactly as before — aborting a working hold because the mic is absent would be the HUD choosing
        // ceremony over the trader's sentence. (`audio_unavailable` stays in the fault vocabulary for the live-ASR
        // path, where no device genuinely means no words; its state is designed and frozen either way.)
        onUnavailable: (reason) =>
          /denied|permission|NotAllowed/i.test(reason)
            ? send({ type: 'fault', kind: 'permission_denied', message: reason })
            : send({ type: 'degrade', lane: 'text', reason }),
      }),
    [options.capture, endUtterance, send, transcriber],
  );

  const press = useCallback(() => {
    const at = new Date().toISOString();
    // Barge-in and a cold press both end in `listening`. The server is told either way: an abandoned turn stops
    // being generated rather than being generated and thrown away.
    if (ctx.state === 'speaking') {
      relay.current?.interrupt();
      send({ type: 'barge_in', at });
    } else {
      send({ type: 'ptt.down', at });
    }
    void capture.start().then((listening) => {
      // No microphone means no speech to detect, so the partial would never appear and the HUD would look dead while
      // the button is held. Push-to-talk still works: the release is what ends the utterance instead of the endpoint.
      if (!listening) send({ type: 'partial', text: transcriber.partial() });
    });
  }, [capture, ctx.state, send, transcriber]);

  const release = useCallback(() => {
    if (ctx.state !== 'listening') return;
    capture.stop();
    setTurns((n) => n + 1);
    // Release ends capture and FINALISES transcription (ESC-003): the machine shows `transcribing` until the
    // transcriber answers, and a transcriber that fails is a recoverable fault, not a hung pill.
    send({ type: 'ptt.up', at: new Date().toISOString() });
    void transcriber
      .finish()
      .then((result) => {
        send({ type: 'endpoint', at: new Date().toISOString(), text: result.text });
      })
      .catch(() => {
        send({
          type: 'fault',
          kind: 'transcription_failed',
          message: 'The utterance could not be transcribed. Hold to talk and try again.',
        });
      });
  }, [capture, ctx.state, send, transcriber]);

  // The assignable hold-to-talk key (ESC-003): keydown starts listening immediately, keyup ends capture and
  // submits. Repeats are one hold; typing surfaces are never captured from; Escape stands down a stuck hold.
  useEffect(() => {
    if (options.enabled === false) return;
    const down = (e: KeyboardEvent) => {
      if (e.repeat || isTyping(e.target)) return;
      if (e.code !== pttKey()) return;
      e.preventDefault();
      press();
    };
    const up = (e: KeyboardEvent) => {
      if (isTyping(e.target)) return;
      if (e.code !== pttKey() && e.code !== 'Escape') return;
      e.preventDefault();
      release();
    };
    window.addEventListener('keydown', down);
    window.addEventListener('keyup', up);
    return () => {
      window.removeEventListener('keydown', down);
      window.removeEventListener('keyup', up);
    };
  }, [options.enabled, press, release]);

  const ask = useCallback(
    async (text: string, confirm?: boolean) => {
      const at = new Date().toISOString();
      send({ type: 'ptt.down', at });
      send({ type: 'endpoint', at, text });
      if (confirm) await runAsk(text, true);
    },
    [runAsk, send],
  );

  const confirm = useCallback(async () => {
    if (!ctx.confirmation) return;
    // Confirming a write goes over the composer call whatever the spoken turn used: the confirmation carries no new
    // text, and the server matches it against the pending write rather than re-generating one.
    await runAsk(`confirm ${ctx.confirmation.intent}`, true);
  }, [ctx.confirmation, runAsk]);

  const clearFault = useCallback(() => send({ type: 'fault.clear' }), [send]);

  const force = useCallback(
    (fragment: Partial<VoiceContext> | null) =>
      setForced(fragment === null ? null : { ...initialVoice(), ...fragment }),
    [],
  );

  const end = useCallback(() => {
    capture.stop();
    relay.current?.close();
    relay.current = null;
    transcriber.reset();
    send({ type: 'end' });
  }, [capture, send, transcriber]);

  // The socket does not outlive the component. A relay session left open holds a turn slot and, in production, holds
  // a provider session that is being billed by the minute.
  useEffect(
    () => () => {
      capture.stop();
      relay.current?.close();
      relay.current = null;
    },
    [capture],
  );

  // A standing fault keeps the HUD visible: a recoverable state nobody can see is not recoverable.
  const active = ctx.state !== 'idle' || ctx.transcript.length > 0 || ctx.fault !== null;
  return useMemo(
    () => ({ ctx, press, release, ask, confirm, end, clearFault, send, force, active }),
    [ctx, press, release, ask, confirm, end, clearFault, send, force, active],
  );
}
